import { a as __rest } from '../../tslib.es6.esm.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useId, useRef } from 'react';
import * as RadixSwitch from '@radix-ui/react-switch';
import { BaseLabel } from '../BaseLabel/BaseLabel.esm.js';
import baseLabelStyles from '../BaseLabel/BaseLabel.styles.esm.js';
import toggleSwitchStyles from './ToggleSwitch.styles.esm.js';
import { cn } from '../../utils/cn.esm.js';

const ToggleSwitch = forwardRef((props, ref) => {
    const generatedId = useId();
    const { id = generatedId, label, disabled = false, align = "left", inverse = false, className, checked, value, onCheckedChange, "aria-label": ariaLabel = "" } = props, others = __rest(props, ["id", "label", "disabled", "align", "inverse", "className", "checked", "value", "onCheckedChange", "aria-label"]);
    const thumbRef = useRef(null);
    const backdropRef = useRef(null);
    // Get the styles from tailwind-variants
    const { container, label: labelStyles, track, handle, backdrop, } = toggleSwitchStyles({
        align,
        inverse,
        disabled,
    });
    const baseLabelClasses = baseLabelStyles({
        inverse,
    });
    const handleCheck = () => {
        if (disabled)
            return;
        onCheckedChange === null || onCheckedChange === void 0 ? void 0 : onCheckedChange(!checked);
    };
    return (jsxs("div", { className: container({ className }), tabIndex: 0, "data-disabled": disabled, onKeyDown: (e) => {
            if (e.code === "Enter" || e.code === "Space") {
                handleCheck();
            }
        }, onClick: () => {
            handleCheck();
        }, children: [!!label && (jsx(BaseLabel, { inverse: inverse, disabled: disabled, className: cn(baseLabelClasses, labelStyles()), tabIndex: -1, children: label })), jsx(RadixSwitch.Root, Object.assign({ ref: ref, id: `toggle-switch-button-${id}`, checked: checked, value: value, className: track(), disabled: disabled }, (label
                ? { "aria-labelledby": `toggle-label-${id}` }
                : { "aria-label": ariaLabel || "Toggle" }), { tabIndex: -1, onMouseEnter: (e) => {
                    var _a;
                    backdropRef.current.setAttribute("data-behavior", "hovering");
                    thumbRef.current.setAttribute("data-behavior", "hovering");
                    (_a = props === null || props === void 0 ? void 0 : props.onMouseEnter) === null || _a === void 0 ? void 0 : _a.call(props, e);
                }, onMouseLeave: (e) => {
                    var _a;
                    thumbRef.current.removeAttribute("data-behavior");
                    backdropRef.current.removeAttribute("data-behavior");
                    (_a = props === null || props === void 0 ? void 0 : props.onMouseLeave) === null || _a === void 0 ? void 0 : _a.call(props, e);
                }, onMouseDown: (e) => {
                    var _a;
                    thumbRef.current.setAttribute("data-behavior", "focusing");
                    backdropRef.current.setAttribute("data-behavior", "focusing");
                    (_a = props === null || props === void 0 ? void 0 : props.onMouseDown) === null || _a === void 0 ? void 0 : _a.call(props, e);
                }, onMouseUp: (e) => {
                    var _a;
                    thumbRef.current.removeAttribute("data-behavior");
                    backdropRef.current.removeAttribute("data-behavior");
                    (_a = props === null || props === void 0 ? void 0 : props.onMouseUp) === null || _a === void 0 ? void 0 : _a.call(props, e);
                } }, (ariaLabel ? { "aria-label": ariaLabel } : {}), others, { children: jsx(RadixSwitch.Thumb, { ref: thumbRef, className: handle(), tabIndex: -1, children: jsx("div", { ref: backdropRef, "data-state": checked ? "checked" : "unchecked", className: backdrop() }) }) }))] }));
});
ToggleSwitch.displayName = "ToggleSwitch";

export { ToggleSwitch };
